import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type PartnerRow } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { Button } from '../../components/ui/Button'
import { Card, CardContent } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Label } from '../../components/ui/Label'

const SUPERADMIN_ROLES = new Set(['superadmin', 'platform_admin'])

export function Partners() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState({ name: '', slug: '' })

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'partners'],
    queryFn: () => api<PartnerRow[]>('/api/v1/admin/partners'),
  })

  const create = useMutation({
    mutationFn: () =>
      api<PartnerRow>('/api/v1/admin/partners', {
        method: 'POST',
        body: JSON.stringify(draft),
      }),
    onSuccess: () => {
      setOpen(false)
      setDraft({ name: '', slug: '' })
      qc.invalidateQueries({ queryKey: ['admin', 'partners'] })
    },
  })

  if (!user?.roles.some((r) => SUPERADMIN_ROLES.has(r))) {
    return <p className="text-sm text-red-600">{t('errors.forbidden')}</p>
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-ink">
          {t('admin.partners.title')}
        </h1>
        <Button variant="primary" onClick={() => setOpen(true)}>
          {t('admin.partners.create')}
        </Button>
      </header>

      {isLoading && <p className="text-sm text-text-muted">{t('common.loading')}</p>}

      {data && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-cream text-xs uppercase text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">{t('admin.partners.columns.name')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.partners.columns.slug')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.partners.columns.created')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((p) => (
                  <tr key={p.id} className="border-t border-warm-mist">
                    <td className="px-4 py-2">{p.name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.slug}</td>
                    <td className="px-4 py-2 text-xs text-text-muted">
                      {p.created_at.slice(0, 10)}
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-sm text-text-muted">
                      {t('common.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Modal
        open={open}
        title={t('admin.partners.create')}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={() => create.mutate()}>
              {t('common.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <Label>{t('admin.partners.columns.name')}</Label>
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
          <div>
            <Label>{t('admin.partners.columns.slug')}</Label>
            <Input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  )
}
